import { Clock, AlertTriangle, Zap } from "lucide-react";

/**
 * TatChip — turn-around-time / age display.
 *
 * Props:
 *   parcel   — parcel object (uses created_at + delivered_at + status)
 *   slaHours — SLA target in hours (defaults per direction below)
 *   compact  — true → smaller pill for table rows
 *
 * Colour logic:
 *   Delivered  → green + "TAT: 3h 12m"
 *   Open, age < 60% SLA  → green + "Age 2h"
 *   Open, 60% ≤ age < 100% SLA → amber + "Age 12h"
 *   Open, age ≥ 100% SLA → red + "SLA breach 3h over"
 */

const DEFAULT_SLA_HOURS = {
  inward: 24,
  outward: 8,
  default: 24,
};

const fmtDuration = (ms) => {
  if (!ms || ms < 0) return "0m";
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

export default function TatChip({ parcel, slaHours, compact = false }) {
  if (!parcel?.created_at) return null;

  const sla = slaHours || DEFAULT_SLA_HOURS[parcel.direction] || DEFAULT_SLA_HOURS.default;
  const slaMs = sla * 3600 * 1000;
  const created = new Date(parcel.created_at).getTime();
  const isDone = parcel.status === "delivered" || parcel.status === "returned";
  const end = (isDone ? new Date(parcel.delivered_at || parcel.updated_at || parcel.created_at).getTime() : Date.now());
  const elapsed = end - created;

  let tone, Icon, label, subtitle;
  if (isDone) {
    if (elapsed <= slaMs) {
      tone = "bg-emerald-50 border-emerald-300 text-emerald-700";
      Icon = Zap;
      label = `TAT ${fmtDuration(elapsed)}`;
      subtitle = "within SLA";
    } else {
      tone = "bg-amber-50 border-amber-400 text-amber-800";
      Icon = Clock;
      label = `TAT ${fmtDuration(elapsed)}`;
      subtitle = `${fmtDuration(elapsed - slaMs)} over SLA`;
    }
  } else {
    if (elapsed >= slaMs) {
      tone = "bg-red-50 border-primary text-primary";
      Icon = AlertTriangle;
      label = `SLA breach`;
      subtitle = `${fmtDuration(elapsed - slaMs)} over`;
    } else if (elapsed >= slaMs * 0.6) {
      tone = "bg-amber-50 border-amber-400 text-amber-800";
      Icon = Clock;
      label = `Age ${fmtDuration(elapsed)}`;
      subtitle = `${fmtDuration(slaMs - elapsed)} left`;
    } else {
      tone = "bg-emerald-50 border-emerald-300 text-emerald-700";
      Icon = Clock;
      label = `Age ${fmtDuration(elapsed)}`;
      subtitle = "fresh";
    }
  }

  if (compact) {
    return (
      <span
        data-testid="tat-chip"
        className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider border rounded px-1.5 py-0.5 mono ${tone}`}
        title={`${label} · ${subtitle} · SLA ${sla}h`}
      >
        <Icon size={9} strokeWidth={2.5} /> {label}
      </span>
    );
  }
  return (
    <div
      data-testid="tat-chip"
      className={`inline-flex items-center gap-2 border rounded-md px-2.5 py-1 ${tone}`}
    >
      <Icon size={13} />
      <div className="leading-tight">
        <div className="text-xs font-semibold">{label}</div>
        <div className="text-[10px] uppercase tracking-widest opacity-80">{subtitle} · SLA {sla}h</div>
      </div>
    </div>
  );
}
